import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RotateCcw, ChevronUp, Flame, Trophy } from "lucide-react";
import { useProgress } from "../context/ProgressContext";
import { ALL_QUESTION_IDS, TOTAL_QUESTIONS, QUIZZES } from "../data/quizzes";

export function ProgressBar() {
  const { answers, reset, combo, stats } = useProgress();
  const [open, setOpen] = useState(false);
  const { answered, correct, xp, accuracy, rank, rankIndex, nextRank, isMax, levelProgress } = stats;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 pointer-events-none">
      <div className="max-w-[1400px] mx-auto px-4 pb-4 flex justify-center">
        <motion.div
          initial={{ y: 90, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="pointer-events-auto bg-[#1C2430] text-[#FBFBF9] shadow-[0_8px_40px_rgba(28,36,48,0.4)] w-full max-w-2xl"
          data-testid="progress-tracker"
        >
          <div className="flex items-center gap-3 md:gap-4 px-4 md:px-5 py-3">
            {/* rank */}
            <div className="shrink-0 flex items-center gap-2">
              <div className="w-8 h-8 border border-[#2F6F4F] flex items-center justify-center text-[#2F6F4F]">
                <Trophy size={15} />
              </div>
              <div className="hidden sm:block leading-none">
                <div className="font-mono-ui text-[9px] uppercase tracking-[0.16em] text-[#FBFBF9]/45">Rank</div>
                <div className="font-semibold text-[12.5px]" data-testid="rank-name">{rank.name}</div>
              </div>
            </div>

            {/* xp bar to next rank */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono-ui text-[9px] uppercase tracking-[0.14em] text-[#FBFBF9]/45">
                  {isMax ? "Max rank" : `→ ${nextRank.name}`}
                </span>
                <span className="font-mono-ui text-[10px] tabular-nums text-[#2F6F4F]" data-testid="xp-value">{xp} XP</span>
              </div>
              <div className="h-[7px] bg-white/12 relative overflow-hidden">
                <motion.div
                  className="absolute inset-y-0 left-0 bg-[#2F6F4F]"
                  animate={{ width: `${levelProgress * 100}%` }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                />
              </div>
            </div>

            {/* streak */}
            <div className="shrink-0 flex items-center gap-1.5 font-mono-ui text-[12px] tabular-nums" data-testid="combo-streak">
              <Flame size={15} className={combo > 1 ? "text-[#D26040]" : "text-[#FBFBF9]/35"} />
              <motion.span key={combo} initial={{ scale: 1.5 }} animate={{ scale: 1 }} className={combo > 1 ? "text-[#D26040] font-bold" : "text-[#FBFBF9]/45"}>
                {combo}
              </motion.span>
            </div>

            <div className="shrink-0 font-mono-ui text-[12px] tabular-nums" data-testid="progress-count">
              {answered}/{TOTAL_QUESTIONS}
            </div>

            <button
              onClick={() => setOpen((o) => !o)}
              className="shrink-0 opacity-70 hover:opacity-100 transition-opacity"
              data-testid="progress-toggle"
              aria-label="Toggle detail"
            >
              <ChevronUp size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          </div>

          <AnimatePresence>
            {open && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                className="overflow-hidden border-t border-white/10"
              >
                <div className="px-5 py-4">
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {ALL_QUESTION_IDS.map((id) => {
                      const a = answers[id];
                      const color = !a ? "bg-white/15" : a.correct ? "bg-[#2F6F4F]" : "bg-[#D26040]";
                      return <div key={id} className={`h-2.5 flex-1 min-w-[14px] ${color}`} title={id} />;
                    })}
                  </div>

                  {/* chapter badges */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {Object.entries(QUIZZES).map(([key, q]) => {
                      const done = q.questions.filter((x) => answers[x.id]).length;
                      const allright = q.questions.every((x) => answers[x.id]?.correct);
                      const finished = done === q.questions.length;
                      return (
                        <div
                          key={key}
                          className={`font-mono-ui text-[10px] uppercase tracking-[0.1em] px-2.5 py-1 border ${
                            finished ? (allright ? "border-[#2F6F4F] text-[#2F6F4F] bg-[#2F6F4F]/10" : "border-white/40 text-[#FBFBF9]") : "border-white/15 text-[#FBFBF9]/40"
                          }`}
                          data-testid={`badge-${key}`}
                        >
                          {q.label} {finished ? (allright ? "★" : "✓") : `${done}/${q.questions.length}`}
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="font-mono-ui text-[11px] text-[#FBFBF9]/55">
                      {answered === 0
                        ? "Answer questions to earn XP and rank up."
                        : `${correct}/${answered} correct · ${accuracy}% accuracy · saved to your session`}
                    </span>
                    <button
                      onClick={reset}
                      className="font-mono-ui text-[11px] uppercase tracking-[0.12em] flex items-center gap-1.5 opacity-70 hover:opacity-100 transition-opacity"
                      data-testid="progress-reset"
                    >
                      <RotateCcw size={12} /> Reset
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}
